import { useCallback, useEffect, useState } from "react";
import { Building2, Pencil, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PageHeader, Loading } from "../../components/shared";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", head_id: "", status: "Active" };

export default function AdminDepartments() {
  const [departments, setDepartments] = useState(null);
  const [staff, setStaff] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [departmentResponse, optionResponse] = await Promise.all([
        api.get("/admin/departments"),
        api.get("/admin/staff/options").catch(() => ({ data: { managers: [] } })),
      ]);
      setDepartments(departmentResponse.data);
      setStaff(optionResponse.data.managers || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load departments");
      setDepartments([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setCreateOpen(true);
  };
  const openEdit = (department) => {
    setEditing(department);
    setForm({
      name: department.name || "",
      description: department.description || "",
      head_id: department.head_id || "",
      status: department.status || "Active",
    });
  };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/departments/${editing.department_id}`, form);
        toast.success("Department updated");
      } else {
        await api.post("/admin/departments", form);
        toast.success("Department created");
      }
      setEditing(null);
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save department");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (department) => {
    if (!window.confirm(`Delete ${department.name}?`)) return;
    try {
      await api.delete(`/admin/departments/${department.department_id}`);
      toast.success("Department deleted");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete department");
    }
  };

  if (!departments) return <Loading />;

  return (
    <div data-testid="admin-departments-page">
      <PageHeader
        title="Departments"
        subtitle="Define the internal teams that own platform operations."
      >
        <Button
          className="rounded-full gap-2"
          data-testid="department-create-button"
          onClick={openCreate}
        >
          <Plus className="w-4 h-4" />
          Add Department
        </Button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="department-list">
        {departments.map((department) => {
          const head = staff.find((person) => person.user_id === department.head_id);
          return (
            <article
              key={department.department_id}
              className="bg-card border rounded-xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
              data-testid={`department-card-${department.department_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-display font-bold text-lg" data-testid={`department-name-${department.department_id}`}>
                      {department.name}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1" data-testid={`department-status-${department.department_id}`}>
                      {department.status}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    aria-label={`Edit ${department.name}`}
                    data-testid={`department-edit-${department.department_id}`}
                    onClick={() => openEdit(department)}
                    size="icon"
                    variant="ghost"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    aria-label={`Delete ${department.name}`}
                    className="text-destructive"
                    data-testid={`department-delete-${department.department_id}`}
                    onClick={() => remove(department)}
                    size="icon"
                    variant="ghost"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mt-5 min-h-10">
                {department.description || "No internal description added."}
              </p>
              <div className="border-t mt-5 pt-3 text-sm">
                <span className="text-muted-foreground">Department head</span>
                <span className="font-medium block mt-0.5" data-testid={`department-head-${department.department_id}`}>
                  {head?.name || "Not assigned"}
                </span>
              </div>
            </article>
          );
        })}
      </div>

      {departments.length === 0 && (
        <div className="border border-dashed rounded-xl p-12 text-center text-muted-foreground" data-testid="department-empty-state">
          No departments yet. Add your first internal team.
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add Department</DialogTitle>
            <DialogDescription>Set up an internal operating team.</DialogDescription>
          </DialogHeader>
          <DepartmentForm form={form} setValue={setValue} staff={staff} />
          <DialogFooter>
            <Button data-testid="department-save-button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Create Department"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="department-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Department</DialogTitle>
            <DialogDescription>Update team ownership and availability.</DialogDescription>
          </DialogHeader>
          <DepartmentForm form={form} setValue={setValue} staff={staff} />
          <DialogFooter>
            <Button data-testid="department-edit-save-button" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DepartmentForm({ form, setValue, staff }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="department-name">Department name</Label>
        <Input
          data-testid="department-name-input"
          id="department-name"
          onChange={(event) => setValue("name", event.target.value)}
          value={form.name}
        />
      </div>
      <div>
        <Label htmlFor="department-description">Internal description</Label>
        <textarea
          className="flex min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
          data-testid="department-description-input"
          id="department-description"
          onChange={(event) => setValue("description", event.target.value)}
          value={form.description}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="department-head">Department head</Label>
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="department-head-select"
            id="department-head"
            onChange={(event) => setValue("head_id", event.target.value)}
            value={form.head_id}
          >
            <option value="">Not assigned</option>
            {staff.map((person) => (
              <option key={person.user_id} value={person.user_id}>{person.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="department-status">Status</Label>
          <select
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="department-status-select"
            id="department-status"
            onChange={(event) => setValue("status", event.target.value)}
            value={form.status}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>
    </div>
  );
}